
import { defineComponent } from '@vue/composition-api';
import { useApiV3 } from '../../../composables/apiV3';
import IllinoisCollapsible from '../components/Collapsible.vue';
import SportIcons from '../components/SportIcons.vue';
import IllinoisTabBar from '../components/TabBar.vue';
import TournamentIcon from '../Icons/TournamentIcon.vue';
import {
  Category,
  CategoriesApiResponse,
  Tournament,
  TournamentsForSportCategoryResponse,
  FeaturedTournament
} from '../../../../types/sport';
import icons from '../components/icons.vue';
import CategoryIcons from '../Icons/Category.vue';
import { TabItem } from '../../../../types/components';
import OddsEventsGroup from '../components/OddsComponents/OddsEventsGroup.vue';
import { addOddToCartV3FromSport } from '@/composables/useOdds';
import { PreviewEventGroup, PreviewEvent } from '../../../../types/sport';

export default defineComponent({
  name: 'SportPage',
  props: {
    sportId: {
      type: String,
      required: true
    }
  },
  components: {
    IllinoisCollapsible,
    SportIcons,
    icons,
    CategoryIcons,
    IllinoisTabBar,
    OddsEventsGroup,
    TournamentIcon
  },
  data() {
    return {
      loading: true,
      error: null as string | null,
      tournamentsByCategory: {} as Record<number, Tournament[]>,
      isLoadingTournaments: {} as Record<number, boolean>,
      expandedCategories: new Set<number>(),
      tabItems: [
        { label: this.$t('featuredTournaments'), value: 'featured' },
        { label: this.$t('topLeague'), value: 'leagues' }
      ] as TabItem[],
      activeTab: 'featured'
    };
  },
  computed: {
    defaultTab() {
      return this.featuredTournaments.length > 0 ? this.activeTab : 'leagues';
    },
    allSports() {
      return this.$store.getters['IllinoisSport/getSports'];
    },
    sportName(): string | null {
      if (this.allSports && this.allSports.length > 0) {
        const sport = this.allSports.find(
          (s: { sportId: number | string; name: string }) =>
            s.sportId.toString() === this.sportId
        );
        return sport ? sport.name : null;
      }
      return null;
    },
    categoryCount() {
      return this.categories.length;
    },
    categories() {
      return this.$store.getters['IllinoisSport/getCategoriesBySport'](
        this.sportId
      );
    },
    featuredTournaments(): FeaturedTournament[] {
      return this.$store.getters['IllinoisSport/getFeaturedTournamentsBySport'](
        this.sportId
      );
    },
    eventsForTournamentFiltered() {
      return (id: string): PreviewEventGroup[] => {
        const groups = this.eventsForTournament(id) || [];
        // Filtra gli eventi che non hanno outcomes attivi (status === 1)
        return groups
          .map(group => ({
            ...group,
            events: Array.isArray(group.events)
              ? group.events.filter((event: PreviewEvent) =>
                  this.hasActiveOutcomesForEvent(event)
                )
              : []
          }))
          .filter(
            group => Array.isArray(group.events) && group.events.length > 0
          );
      };
    }
  },
  watch: {
    sportId: {
      handler(newSportId, oldSportId) {
        if (newSportId !== oldSportId) {
          // Resettiamo i dati derivati
          this.tournamentsByCategory = {};
          this.isLoadingTournaments = {};
          this.expandedCategories.clear();

          // Ricarichiamo i dati con il nuovo sportId
          this.ensureSportsLoaded();
          this.fetchCategories();
        }
      },
      immediate: true // Esegue il watcher anche al mount iniziale
    },
    categories: {
      handler(newCategories) {
        if (
          newCategories &&
          newCategories.length > 0 &&
          this.expandedCategories.size === 0
        ) {
          // Apri automaticamente i primi 3 elementi
          const firstThree = newCategories.slice(0, 3);
          firstThree.forEach(category => {
            this.expandedCategories.add(category.categoryId);
            // Carica i tornei per queste categorie
            if (!this.tournamentsByCategory[category.categoryId]) {
              this.fetchTournamentsForCategory(category.categoryId);
            }
          });
        }
      },
      immediate: true
    },
    featuredTournaments: {
      immediate: true,
      handler(newTournaments) {
        if (newTournaments && newTournaments.length > 0) {
          this.fetchTournamentEvents();
        }
      }
    }
  },
  mounted() {
    window.parent.postMessage(
      {
        type: 'illinoisSportOpened',
        sportId: this.sportId
      },
      '*'
    );
    // Carica i tornei featured per questo sport
    this.$store.dispatch(
      'IllinoisSport/fetchFeaturedTournamentsBySportIfNeeded',
      {
        sportId: this.sportId
      }
    );
  },
  methods: {
    addOddToCartV3FromSport,
    ensureSportsLoaded(): Promise<void> {
      if (this.allSports && this.allSports.length > 0) {
        return Promise.resolve();
      }
      return this.$store.dispatch('IllinoisSport/fetchSportsIfNeeded');
    },
    /*  async fetchCategories() {
      this.loading = true;
      this.error = null;

      try {
        const api = useApiV3();
        const response: CategoriesApiResponse = await api.getCategoriesForSport(
          this.sportId
        );

        if (response && response.categories) {
          // Assicuriamoci che categories sia un array
          this.categories = Array.isArray(response.categories)
            ? response.categories
            : Object.values(response.categories);

          console.log('Categorie caricate:', this.categories.length);
        } else {
          this.error = 'Formato risposta non valido';
        }
      } catch (err) {
        this.error = 'Errore nel caricamento';
        console.error(err);
      } finally {
        this.loading = false;
      }
    }, */
    eventsForTournament(id: string) {
      return this.$store.getters['IllinoisSport/getPreviewByTournament'](id);
    },
    hasEventsForTournament(id: string) {
      const arr = this.eventsForTournament(id);
      return Array.isArray(arr) && arr.length > 0;
    },
    isTournamentEventsLoaded(id: string) {
      return this.loadedTournamentEvents.has(id);
    },

    hasActiveOutcomesForEvent(event: any): boolean {
      if (!event || !Array.isArray(event.odds)) return false;
      return event.odds.some((odd: any) =>
        Array.isArray(odd?.outcome)
          ? odd.outcome.some((o: any) => o.status === 1)
          : false
      );
    },
    t(key: string): string {
      return this.$t(key) as string;
    },

    async fetchTournamentEvents() {
      if (!this.featuredTournaments || this.featuredTournaments.length === 0)
        return;
      const promises = this.featuredTournaments.map(tournament => {
        try {
          return this.$store.dispatch(
            'IllinoisSport/fetchPreviewByTournament',
            {
              sportId: String(tournament.sportId),
              categoryId: String(tournament.categoryId),
              tournamentId: String(tournament.tournamentId)
            }
          );
        } catch (error) {
          console.error(
            `Errore nel caricamento degli eventi per il torneo ${tournament.tournamentName}:`,
            error
          );
          return Promise.resolve();
        }
      });
      // Esegui in parallelo senza bloccare la UI; errori isolati non impediscono gli altri
      await Promise.allSettled(promises);
    },
    async fetchCategories() {
      this.loading = true;
      this.error = null;
      try {
        await this.$store.dispatch('IllinoisSport/fetchCategoriesIfNeeded', {
          sportId: this.sportId
        });
      } catch (err) {
        this.error = 'Errore nel caricamento';
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    async fetchTournamentsForCategory(categoryId: number) {
      if (this.isLoadingTournaments[categoryId]) return;

      // Imposta stato di caricamento
      this.$set(this.isLoadingTournaments, categoryId, true);

      try {
        const api = useApiV3();
        const response: TournamentsForSportCategoryResponse =
          await api.getTournamentsForSportCategory(
            this.sportId.toString(),
            categoryId.toString()
          );

        if (response && response.tournaments) {
          // Salva i tornei per questa categoria
          this.$set(
            this.tournamentsByCategory,
            categoryId,
            response.tournaments
          );
        } else {
          // Se non ci sono tornei, imposta un array vuoto
          this.$set(this.tournamentsByCategory, categoryId, []);
        }
      } catch (err) {
        console.error(
          `Errore nel caricamento dei tornei per la categoria ${categoryId}:`,
          err
        );
        // In caso di errore, imposta un array vuoto
        this.$set(this.tournamentsByCategory, categoryId, []);
      } finally {
        // Rimuove lo stato di caricamento
        this.$set(this.isLoadingTournaments, categoryId, false);
      }
    },
    isInitialOpen(index: number): boolean {
      return index < 3;
    },
    handleToggle(isOpen: boolean, categoryId: number): void {
      if (isOpen) {
        // Aggiunge la categoria a quelle espanse
        this.expandedCategories.add(categoryId);

        // Se i tornei non sono ancora stati caricati per questa categoria, li carica
        if (!this.tournamentsByCategory[categoryId]) {
          this.fetchTournamentsForCategory(categoryId);
        }
      } else {
        // Rimuove la categoria da quelle espanse
        this.expandedCategories.delete(categoryId);
      }
    }
  }
});
